/**
 * Dashboard Header Component
 * Displays user info, business name, and quick actions
 */

import React from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCcw, Plus, Eye } from 'lucide-react';
import { INDUSTRY_CONFIG, getIndustryConfig } from '@/config/dashboardConfig';
import { DashboardCustomizationTrigger } from '../DashboardCustomizationPanel';
import DataFreshnessIndicator from '../DataFreshnessIndicator';
import NotificationCenter from '@/components/NotificationCenter';

export const DemoPreviewBanner = ({ demoPreview, onClose }) => {
  if (!demoPreview?.is_demo_preview) return null;
  
  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <Eye className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Demo Preview Mode</p>
          <p className="text-sm text-white/80">You&apos;re viewing this dashboard as a demo for {demoPreview.company_name}</p>
        </div>
      </div>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={onClose}
        className="bg-white/20 border-white/30 text-white hover:bg-white/30 whitespace-nowrap"
      >
        Close Preview
      </Button>
    </div>
  );
};

export const DashboardHeader = ({
  userName,
  businessName,
  roleName,
  industry,
  onRefresh,
  onAddTask
}) => {
  const industryConfig = getIndustryConfig(industry);
  const IndustryIcon = industryConfig?.icon;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-center gap-3 sm:gap-4">
        <Avatar className="h-12 w-12 sm:h-14 sm:w-14 border-2 border-primary/20 shadow-lg">
          <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/30 text-primary font-bold">
            {userName.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div>
          {/* Business Name - Primary with Premium Font */}
          <h1 className="text-xl sm:text-2xl font-display font-bold flex items-center gap-2">
            {IndustryIcon && <IndustryIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />}
            {businessName}
          </h1>
          {/* Personal Name + Role - Secondary */}
          <p className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
            <span className="font-medium text-foreground">{userName}</span>
            <span className="hidden sm:inline">•</span>
            <Badge className="bg-primary/10 text-primary border-primary/20">{roleName}</Badge>
            <span className="hidden sm:inline">•</span>
            <span className={`capitalize px-2 py-0.5 rounded-full text-xs ${industryConfig?.gradient ? 'bg-gradient-to-r text-white ' + industryConfig.gradient : 'bg-slate-100'}`}>
              {industryConfig?.name || industry}
            </span>
          </p>
        </div>
      </div>
      
      {/* Quick Actions - Desktop */}
      <div className="hidden sm:flex items-center gap-2">
        <NotificationCenter />
        <DashboardCustomizationTrigger />
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
        <Button size="sm" onClick={onAddTask}>
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </Button>
      </div>
    </div>
  );
};

export const MobileQuickStats = ({ 
  completedCount, 
  pendingCount, 
  highPriorityCount, 
  onAddTask 
}) => (
  <div className="flex sm:hidden items-center justify-between bg-white/60 dark:bg-slate-800/60 rounded-xl p-3 border border-slate-200/50">
    <div className="text-center">
      <p className="text-lg font-bold text-emerald-600">{completedCount}</p>
      <p className="text-[10px] text-muted-foreground">Done</p>
    </div>
    <div className="text-center">
      <p className="text-lg font-bold text-amber-600">{pendingCount}</p>
      <p className="text-[10px] text-muted-foreground">Pending</p>
    </div>
    <div className="text-center">
      <p className="text-lg font-bold text-red-600">{highPriorityCount}</p>
      <p className="text-[10px] text-muted-foreground">Urgent</p>
    </div>
    <Button size="sm" className="ml-2" onClick={onAddTask}>
      <Plus className="w-4 h-4" />
    </Button>
  </div>
);

export default DashboardHeader;
